import { Component, HostListener, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { WindowEnums } from 'src/app/enum/window-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ProfileUserTableModel } from 'src/app/models/tables/authentication/profile-user-table.model';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { ProfileUserService } from 'src/app/services/authentication/profile-user.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-profile-user',
  templateUrl: './profile-user.component.html',
  styleUrls: ['./profile-user.component.scss'],
  standalone: false
})
export class ProfileUserComponent implements OnInit {

  ascendant: boolean = true;
  columnSort: string | null = null;
  filters: ColumnResponseModel[] = [];
  data: PagedResponseModel<ProfileUserTableModel> = new PagedResponseModel<ProfileUserTableModel>();
  header: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Perfiles';
  page: number = 1;
  permissionUpdatable: boolean = false;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    },
  ];
  url: string = "";
  userAuthenticated: UserModel = new UserModel();

  constructor(private confirmDialogService: ConfirmDialogService,
    private headerService: HeaderService,
    private optionWindowService: OptionWindowService,
    private profileUserService: ProfileUserService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.getPermission();
    const filters = localStorage.getItem('profileUser');
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForConsultation(),
      this.getProfileUsersWithPagination(),
    ]);
    this.headerService.setTitle(this.nameWindow);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async deleteUser(profileUser: ProfileUserTableModel) {
    if (this.permissionUpdatable) {
      if (!await this.confirmDialogService.confirm(
        `¿Realmente desea eliminar el pefil ${profileUser.name}?`
      )) return;
      try {
        await firstValueFrom(this.profileUserService.deleteProfileUser(profileUser.profileUserId));
        await this.getProfileUsersWithPagination();
        this.toastHandlerService.showToast(
          'success',
          'El perfil fue eliminado exitosamente.'
        );
      } catch (error) {
        console.error('Error al eliminar el perfil.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para eliminar perfiles. Comuníquese con el administrador del sistema.'
      );
    }
  }

  async exportConsultation() {
    try {
      const blob = await firstValueFrom(this.profileUserService.exportConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Perfiles.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los perfiles.', error);
    }
  }

  async getColumnsForConsultation() {
    try {
      if (this.header.length === 0) {
        this.header = await firstValueFrom(this.profileUserService.getColumnsForConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getPermission() {
    let url = this.router.url as string
    url = url.substring(1);
    this.url = url;
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos por perfil.', error);
    }
    this.getPermissionsForUserByWindow();
  }

  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(
      this.userAuthenticatedService.userObservable.pipe(
        filter(user => user && user.userId !== 0)
      )
    );
    this.permissionsUser = await firstValueFrom(this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.ProfileUser));
  }

  async getProfileUsersWithPagination() {
    try {
      this.data = await firstValueFrom(this.profileUserService.getProfileUsersForConsultation(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters
      ));
    } catch (error) {
      console.error('Error al buscar los perfiles.', error);
    }
  }

  newUser() {
    if (this.permissionUpdatable) {
      this.router.navigate(['/profile-user/register']);
    } else {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para crear perfiles. Comuníquese con el administrador del sistema.'
      );
    }
  }

  onAction(event: { action: string, item?: ProfileUserTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/profile-user/register/${event.item.id}`]);
    }
    if (event.action === 'delete' && event.item) {
      this.deleteUser(event.item);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getProfileUsersWithPagination();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  reloadList(reload: boolean) {
    if (reload) {
      this.getProfileUsersWithPagination();
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}
